import { LAYERED_ENGRAVED_SHAPES } from "./layeredEngraving";
import type { Shape, ShapeInterface } from "~/types/grid.types";
import { SHAPES as SHAPES_FOR_3D, ENGRAVED_3D_SHAPES } from "./shapesFor3D";
import { SHAPES as SHAPES_FOR_45DEG } from "./shapesFor45deg";
import { SHAPES as SHAPES_FOR_FREEFORM } from "./shapesForFreeform";
import { SHAPES as SHAPES_FOR_HEX } from "./shapesForHex";
import { SHAPES as SHAPES_FOR_HEX_90 } from "./shapesForHex90";
import { SHAPES as SHAPES_FOR_HEX_SPACED } from "./shapesForHexSpaced";
import { SHAPES as SHAPES_FOR_SQUARE } from "./shapesForSquare";
import { canonicalizePeakId } from "./peakTileCompat";

// Re-exports — implementations moved to dedicated files to keep this under 400 LOC.
export { getShapeListBounds } from "./shapeBounds";
export {
  loadVariations,
  getColorVariation,
  debugVariationLookup,
} from "./variationMatching";

const allShapes = [
  ...LAYERED_ENGRAVED_SHAPES,
  ...SHAPES_FOR_SQUARE,
  ...SHAPES_FOR_45DEG,
  ...SHAPES_FOR_HEX,
  ...SHAPES_FOR_HEX_90,
  ...SHAPES_FOR_HEX_SPACED,
  ...SHAPES_FOR_FREEFORM,
  ...SHAPES_FOR_3D,
  ...ENGRAVED_3D_SHAPES,
];
const allShapesWithChildren = allShapes.reduce((acc: Shape[], shape) => {
  acc.push(shape);
  if (shape.children) acc.push(...shape.children);
  return acc;
}, []);

export const getShapeFromPrefix = (prefix: string): Shape | undefined => {
  const canonical = canonicalizePeakId(prefix);
  return allShapesWithChildren.find((shape) => shape.prefix === canonical);
};

export const getShapeById = (id: string): Shape | undefined => {
  const canonical = canonicalizePeakId(id);
  return allShapesWithChildren.find((shape) => shape.id === canonical);
};

export const findShapeInListById = (
  id: string,
  shapeList: Shape[],
): Shape | undefined => {
  const searchInShapes = (shapes: Shape[]): Shape | undefined => {
    for (const shape of shapes) {
      if (shape.id === id) return shape;
      if (shape.children?.length) {
        const foundInChildren = searchInShapes(shape.children);
        if (foundInChildren) return foundInChildren;
      }
    }
    return undefined;
  };
  return searchInShapes(shapeList);
};

/**
 * Finds the parent shape that has the given shapeId as a child
 */
export const getParentShape = (shapeId: string): Shape | undefined => {
  return allShapes.find(
    (s) => s.children && s.children.some((child) => child.id === shapeId),
  );
};

const LEGEND_ALPHABET = "123456789ABCDEFGHIJKLMNPQRSTUVWXYZ";
const LEGEND_LENGTH = 2;
const LEGEND_BASE = LEGEND_ALPHABET.length;
const MAX_LEGEND_VALUE = Math.pow(LEGEND_BASE, LEGEND_LENGTH); // 34^2 = 1,156

const encodeLegendValue = (value: number) => {
  const limitedValue = value % MAX_LEGEND_VALUE || 1;
  if (limitedValue <= 0) return LEGEND_ALPHABET[0].repeat(LEGEND_LENGTH);

  let current = limitedValue;
  let encoded = "";
  while (current > 0) {
    const index = current % LEGEND_BASE;
    encoded = `${LEGEND_ALPHABET[index]}${encoded}`;
    current = Math.floor(current / LEGEND_BASE);
  }
  return encoded
    .padStart(LEGEND_LENGTH, LEGEND_ALPHABET[0])
    .slice(0, LEGEND_LENGTH);
};

const hashVariantId = (variantId: string) => {
  let hash = 0;
  for (let i = 0; i < variantId.length; i++) {
    hash = (hash * 131 + variantId.charCodeAt(i)) >>> 0;
  }
  return hash || 1;
};

export const generateLegendID = (
  variantId?: string,
  designScopeId?: string | null,
  reroll = 0,
) => {
  if (!variantId) return LEGEND_ALPHABET[0].repeat(LEGEND_LENGTH);
  const rerollSuffix = reroll > 0 ? `::${reroll}` : "";
  const variantKey = `${variantId}${rerollSuffix}`;
  const scopeKey = designScopeId
    ? `${designScopeId}:${variantKey}`
    : variantKey;
  const hashedValue = hashVariantId(scopeKey);
  return encodeLegendValue(hashedValue);
};

export const convertNominalToActualSize = (shapeList: ShapeInterface[]) => {
  const DEFAULT_RATIO = 0.99;

  const CONVERT_RATIOS: Record<string, { width: number; height: number }> = {
    LargeSquare: { width: 0.98958, height: 0.98958 },
    LargeHalflap: { width: 0.98958, height: 0.98958 },
    Shiplap: { width: 0.98958, height: 0.98958 },
    Hex: { width: 0.98167, height: 0.98167 },
    Diamond: { width: 0.98167, height: 0.98167 },
    LargeDiamond: { width: 0.98167, height: 0.98167 },
    HalfCircle: { width: 0.99, height: 0.99 },
    SmallHalfCircle: { width: 0.99, height: 0.99 },
    LargeHalfCircle: { width: 0.98958, height: 0.98958 },
    SmallTriangle: { width: 0.98333, height: 0.98333 },
    LargeTriangle: { width: 0.98167, height: 0.98167 },
  };

  const conversions = { width: [] as number[], height: [] as number[] };

  const convertedList: ShapeInterface[] = shapeList.map((shape: any) => {
    try {
      const shapeBase = shape.metadata?.shape_id
        ? getShapeById(shape.metadata?.shape_id)
        : null;

      // Slats are already drawn to their physical dimensions, including saved
      // sizes from earlier catalog revisions. Do not apply the felt-tile shrink.
      if (shapeBase?.finishFamily === 'wood-slat') {
        conversions.width.push(1);
        conversions.height.push(1);
        return {
          ...shape,
          width: shape.width ?? shape.attrs?.width ?? shapeBase.w,
          height: shape.height ?? shape.attrs?.height ?? shapeBase.h,
          x: shape.x ?? shape.attrs?.x ?? 0,
          y: shape.y ?? shape.attrs?.y ?? 0,
        };
      }

      const width = shapeBase?.w || shape.width || shape.attrs.width;
      const height = shapeBase?.h || shape.height || shape.attrs.height;
      const x = shape.x || shape.attrs.x;
      const y = shape.y || shape.attrs.y;

      if (shapeBase && CONVERT_RATIOS[shapeBase.prefix]) {
        let forcedWidth = width;
        let forcedHeight = height;

        if (
          ["HalfCircle", "LargeHalfCircle", "SmallHalfCircle"].includes(
            shapeBase.prefix,
          )
        ) {
          forcedWidth = Math.min(width, height);
          forcedHeight = Math.max(width, height);
        }
        if (["SmallTriangle", "LargeTriangle"].includes(shapeBase.prefix)) {
          forcedWidth = Math.max(width, height);
          forcedHeight = Math.min(width, height);
        }

        const actualSize = {
          width: forcedWidth * CONVERT_RATIOS[shapeBase.prefix].width,
          height: forcedHeight * CONVERT_RATIOS[shapeBase.prefix].height,
          x: x * CONVERT_RATIOS[shapeBase.prefix].width,
          y: y * CONVERT_RATIOS[shapeBase.prefix].height,
        };
        conversions.width.push(CONVERT_RATIOS[shapeBase.prefix].width);
        conversions.height.push(CONVERT_RATIOS[shapeBase.prefix].height);
        return { ...shape, ...actualSize };
      }

      conversions.width.push(DEFAULT_RATIO);
      conversions.height.push(DEFAULT_RATIO);
      return {
        ...shape,
        width: width * DEFAULT_RATIO,
        height: height * DEFAULT_RATIO,
        x: x * DEFAULT_RATIO,
        y: y * DEFAULT_RATIO,
      };
    } catch (error) {
      console.error("Error converting shape", error);
      return shape;
    }
  });

  const avgConversion = {
    width:
      conversions.width.reduce((acc, curr) => acc + curr, 0) /
      conversions.width.length,
    height:
      conversions.height.reduce((acc, curr) => acc + curr, 0) /
      conversions.height.length,
  };

  return [convertedList, avgConversion];
};

export const is45deg = (rotation: number) => {
  return ![0, 90, 180, 270].includes(rotation) && rotation % 45 === 0;
};

function camelToTitleCase(str: string) {
  const withSpaces = str.replace(/([A-Z])/g, " $1");
  return withSpaces
    .replace(/^./, (match) => match.toUpperCase())
    .replace(/\b\w/g, (match) => match.toUpperCase())
    .trim();
}

export const writeColorLabel = (label: string) => {
  return camelToTitleCase(label);
};

export const writeShapeLabel = (label: string) => {
  if (label === "4Square") label = "FourSquare";
  if (label.toLowerCase() === "blank") label = "Square";
  if (label.toLowerCase() === "half") label = "Halflap";
  if (label === "Diamond") label = "SmallDiamond";

  if (
    label.startsWith("Cube12x") ||
    label.startsWith("Rect12x") ||
    label.startsWith("Cube36x")
  ) {
    const sizes = { Cube12: "Small", Rect12: "Medium", Cube36: "Large" };
    let size = label.split("x")[0];
    size = size ? `${sizes[size as keyof typeof sizes]} ` : "";
    let depth = label.split("x")[2];
    depth = depth ? `${depth}" ` : "";
    return `${size}${depth}3D Acoustic Boxes`;
  }

  return camelToTitleCase(label);
};
